/**
 * Markdown-it plugin for parsing @-mention references to markup shapes
 * Feature: 049-critique-photo-markup
 *
 * Matches patterns like @1, @2, @3 (single digits 1-9)
 * Renders them with special styling for visual distinction
 */

import MarkdownIt from 'markdown-it';

/**
 * Markdown-it plugin to parse @-mentions for shape references (@1 through @9)
 */
export function markdownShapeReferencesPlugin(md: MarkdownIt): void {
  /**
   * Parse @-mention inline token
   * Matches @1 through @9 (single digit only)
   */
  function parseShapeReference(state: any, silent: boolean): boolean {
    const pos = state.pos;
    const max = state.posMax;

    // Check for @ character at current position
    if (state.src.charCodeAt(pos) !== 0x40 /* @ */) {
      return false;
    }

    // Check if there's at least one more character
    if (pos + 1 >= max) {
      return false;
    }

    // Check if next character is a digit 1-9
    const digitCode = state.src.charCodeAt(pos + 1);
    if (digitCode < 0x31 /* 1 */ || digitCode > 0x39 /* 9 */) {
      return false;
    }

    // Check that it's not followed by another digit (we only want single digits)
    if (pos + 2 < max) {
      const nextChar = state.src.charCodeAt(pos + 2);
      if (nextChar >= 0x30 /* 0 */ && nextChar <= 0x39 /* 9 */) {
        return false; // Don't match @10, @21, etc.
      }
    }

    // If silent mode, just return true (used for validation)
    if (silent) {
      return true;
    }

    // Create token for @-mention
    const token = state.push('shape_reference', '', 0);
    token.content = state.src.slice(pos, pos + 2); // @1, @2, etc.
    token.meta = {
      shapeNumber: parseInt(state.src[pos + 1], 10),
    };

    // Advance position by 2 characters (@X)
    state.pos += 2;

    return true;
  }

  /**
   * Render @-mention as HTML with special styling
   */
  function renderShapeReference(tokens: any[], idx: number): string {
    const token = tokens[idx];
    const shapeNumber = token.meta?.shapeNumber || 0;

    return `<span class="shape-reference" data-shape="${shapeNumber}">${token.content}</span>`;
  }

  // Register inline rule
  md.inline.ruler.push('shape_reference', parseShapeReference);

  // Register renderer
  md.renderer.rules.shape_reference = renderShapeReference;
}

/**
 * Create a MarkdownIt instance with shape reference plugin enabled
 */
export function createMarkdownWithShapeReferences(): MarkdownIt {
  const md = new MarkdownIt({
    html: false, // Disable HTML tags for security
    linkify: true, // Auto-detect URLs
    typographer: true, // Smart quotes, etc.
  });

  // Enable shape reference plugin
  md.use(markdownShapeReferencesPlugin);

  return md;
}

/**
 * CSS styles for shape references
 * Add these to your global CSS or component styles
 *
 * NOTE: The actual styles are defined in index.css using CSS variables.
 * This export is kept for backward compatibility.
 */
export const SHAPE_REFERENCE_STYLES = `
.shape-reference {
  display: inline-block;
  padding: 0.125rem 0.375rem;
  margin: 0 0.125rem;
  font-weight: 600;
  color: rgb(var(--color-shape-reference));
  background-color: rgb(var(--color-shape-reference-bg));
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
`;
